/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2018 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
import {
  getTotalPages,
  getFileName,
  isSinglePageMode,
  isFullScreenOrFitOnePageMode
} from "selectors/documentSelectors";
import {
  isZoomFeatureEnabled,
  getJSEngine,
  getShouldUseModernViewer,
  getCurrRHPFeature,
  getSupportPDFJavaScriptPromise
} from "selectors/appSelectors";
import { getPageZoomLevel } from "selectors/zoom";
import { getPageOrigWidth, getPageOrigHeight } from "selectors/pageSelectors";
import { lhpFeatures, rhpDropinIds } from "constants/enums";
import { publicApiNames } from "publicApis/constants";
import { getInterpretedActions } from "utils/pdfActionUtils";
import { SecurityUtils } from "dc-preview-utils/src/Utils/SecurityUtils";
import { getCurrentPageIndex } from "selectors/pageNumber";
import {
  getTotalHeightTillPageTop,
  getYPosition,
  getXPosition,
  getPagesViewXPos,
  getPagesViewYPos
} from "selectors/pagedViewState";
import { isArray, isNumber, isUndefined, isFinite, noop } from "utils/lodashUtils";
import { dialogApi, dialogTypes } from "components/dialogs";
import docApi from "api";
import { apiFactory } from "api/external/ApiFactory";
import { currLHPFeature } from "selectors/lhpSelectors";
import { printHandler } from "print/printHandler";
import { triggerPDFAction } from "./listenToPDFAction";
import { triggerHyperlinkClickEvent } from "publicApis/Events/events";
import { downloadAssetFromDocApi } from "utils/DownloadUtility";
import { handleSubmitForm } from "submitFormAction/submitFormActionHelper";
import { getPageOverlayApis } from "selectors/overlayDropinSelectors";
import { ACROFORM_CLIENT_NAME } from "constants/constants";
import { logJSActionsData } from "logging";
import { recordPerformanceTimings } from "utils/performance";

export const openUrl = (url, extraParams) => {
  dialogApi.show(dialogTypes.OPEN_LINK_DIALOG, {
    url,
    openLink: () => {
      SecurityUtils.openUrl(url);
      triggerHyperlinkClickEvent(extraParams, url);
    },
    cancelDialog: () => dialogApi.hide()
  });
};

export const getFirstURLActionInJSON = pdfActionsJSON => {
  const pdfActions = getInterpretedActions(pdfActionsJSON);
  if (isArray(pdfActions) && pdfActions.length) {
    for (let i = 0; i < pdfActions.length; i++) {
      const pdfAction = pdfActions[i];
      if (pdfAction.command === "GoToURL" && pdfAction.url) {
        return pdfAction.url;
      }
    }
  }
  return null;
};

export const performPdfActions = (stores, pdfActions, extraParams) => {
  const response = { success: true };
  const interpretedPDFActions = getInterpretedActions(pdfActions, extraParams);
  if (isArray(interpretedPDFActions) && interpretedPDFActions.length) {
    performAction(stores, interpretedPDFActions, extraParams, response);
  } else {
    response.success = false;
  }
  return response;
};

export const getNewYPosition = (stores, pageIndex, top) => {
  let newYPosition = 0;
  if (isNumber(pageIndex) && pageIndex >= 0 && pageIndex < getTotalPages(stores)) {
    newYPosition = getTotalHeightTillPageTop(stores, pageIndex);
    if (top >= 0 && top < getPageOrigHeight(stores, pageIndex)) {
      newYPosition += top * getPageZoomLevel(stores, pageIndex);
    }
  }
  return newYPosition;
};

export const getNewXPosition = (stores, pageIndex, left) => {
  let newXPosition = 0;
  if (isNumber(pageIndex) && pageIndex >= 0 && pageIndex < getTotalPages(stores)) {
    if (left >= 0 && left < getPageOrigWidth(stores, pageIndex)) {
      newXPosition = left * getPageZoomLevel(stores, pageIndex);
    }
  }
  return newXPosition;
};

export const setNewLocation = (stores, pageIndex, left, top) => {
  let newXPosition = 0;
  let newYPosition = 0;
  if (isSinglePageMode(stores) || isFullScreenOrFitOnePageMode(stores)) {
    stores.pagedViewState.goToPage(pageIndex + 1);
  } else {
    const prevX = getPagesViewXPos(stores);
    const prevY = getPagesViewYPos(stores);
    newXPosition = !isUndefined(left) ? getXPosition(stores, pageIndex, left) : prevX;
    newYPosition = !isUndefined(top) ? getYPosition(stores, pageIndex, top) : prevY;
    if (prevX === newXPosition && prevY === newYPosition) return;
    stores.pagedViewState.scrollPagesView(newXPosition, newYPosition);
  }
  triggerPDFAction({
    command: "setNewLocation",
    pageIndex,
    x: newXPosition,
    y: newYPosition
  });
};

const handleGoToAction = (stores, action, extraParams) => {
  const { pageNum, x, y } = action;
  if (!isUndefined(pageNum) && !isUndefined(x) && !isUndefined(y)) {
    // TODO: Fix this properly https://jira.corp.adobe.com/browse/ADCS-31718
    setTimeout(() => {
      setNewLocation(stores, pageNum, x, y);
    }, 10);
  }
};

const handleURIAction = (stores, action, extraParams) => {
  const { url } = action;
  if (url) {
    openUrl(url, extraParams);
  }
  triggerPDFAction(action);
};

const handleChangeZoomLevel = (stores, action, extraParams) => {
  const { zoomLevel } = action;
  if (!isUndefined(zoomLevel) && isZoomFeatureEnabled(stores)) {
    stores.zoom.performZoomAction({ newZoomLevel: zoomLevel });
  }
  isZoomFeatureEnabled(stores) && triggerPDFAction(action);
};

const handleChangePageViewModifier = (stores, action, extraParams) => {
  const { pageViewModifier } = action;
  if (pageViewModifier && isZoomFeatureEnabled(stores)) {
    stores.zoom.setPageViewModifier({
      newPageViewModifier: pageViewModifier
    });
  }
  isZoomFeatureEnabled(stores) && triggerPDFAction(action);
};

const mapPageNameToPageIndex = (stores, pageName) => {
  let pageIndex = getCurrentPageIndex(stores);
  const totalPages = getTotalPages(stores);
  switch (pageName) {
    case "FirstPage":
      pageIndex = 0;
      break;

    case "LastPage":
      pageIndex = totalPages - 1;
      break;

    case "NextPage":
      if (pageIndex !== totalPages - 1) {
        pageIndex += 1;
      }
      break;

    case "PrevPage":
      if (pageIndex !== 0) {
        pageIndex -= 1;
      }
      break;

    default:
      break;
  }
  return pageIndex;
};

const handleGoToPage = (stores, action, extraParams) => {
  let pageIndex = getCurrentPageIndex(stores);
  if ("pageName" in action) {
    pageIndex = mapPageNameToPageIndex(stores, action.pageName);
  } else if ("pageIndex" in action && isFinite(action.pageIndex)) {
    pageIndex = action.pageIndex;
  }
  stores.pagedViewState.goToPage(pageIndex + 1);
  triggerPDFAction({ command: "GoToPage", pageIndex });
};

const handleJavaScriptAction = (stores, action, extraParams) => {
  let jsEngine = getJSEngine(stores);
  if (!jsEngine || !jsEngine.isInitialized()) {
    initJSEngine(stores);
    logJSActionsData({ jsEngineNotInitialised: true });
    return;
  }
  try {
    jsEngine.executeScriptSync(action.jsScript, extraParams);
  } catch (error) {
    console.error(error);
  }
  triggerPDFAction(action);
};

const getRHPDropinId = id => {
  switch (id) {
    case lhpFeatures.THUMBNAIL:
      return rhpDropinIds.THUMBNAILS;

    case lhpFeatures.BOOKMARKS:
      return rhpDropinIds.BOOKMARKS;

    case lhpFeatures.ATTACHMENTS:
      return rhpDropinIds.ATTACHMENTS;
  }
  return;
};

const handleUpdateSideBarPlugin = async (stores, action, extraParams) => {
  const { plugin } = action;
  if (isUndefined(plugin)) return;

  let finalPlugin = lhpFeatures.NONE;
  if (!getShouldUseModernViewer(stores)) {
    try {
      const newLHPFeature = currLHPFeature(stores) === plugin ? lhpFeatures.NONE : plugin;
      stores.lhpStore.setCurrLHPFeature(newLHPFeature);
      finalPlugin = newLHPFeature;
    } catch (error) {
      stores.lhpStore.setCurrLHPFeature(lhpFeatures.NONE);
    }
  } else {
    const rhpApis = await apiFactory.getExposedApis("preview", [publicApiNames.RHP], true, true);
    try {
      if (getCurrRHPFeature(stores) !== getRHPDropinId(plugin)) {
        rhpApis[publicApiNames.RHP].openPanel(getRHPDropinId(plugin));
        finalPlugin = plugin;
      } else {
        rhpApis[publicApiNames.RHP].closePanel();
      }
    } catch (e) {
      rhpApis[publicApiNames.RHP].closePanel();
    }
  }
  triggerPDFAction({ command: "UpdateSideBarPlugin", plugin: finalPlugin });
};

const handleSavePDF = (stores, action, extraParams) => {
  downloadAssetFromDocApi(stores, { triggeredFrom: "pdfAction" })
    .then(() => {
      const fileSubtype = docApi.getFiletype();
      const fileName = getFileName(stores);
      triggerPDFAction({ command: "SavePDF", fileSubtype, fileName });
    })
    .catch(noop);
};

const handleZoomIn = (stores, action, extraParams) => {
  isZoomFeatureEnabled(stores) && stores.zoom.zoomIn();
  isZoomFeatureEnabled(stores) && triggerPDFAction(action);
};

const handleZoomOut = (stores, action, extraParams) => {
  isZoomFeatureEnabled(stores) && stores.zoom.zoomOut();
  isZoomFeatureEnabled(stores) && triggerPDFAction(action);
};

const handlePrint = (stores, action, extraParams) => {
  printHandler.print({ triggeredFrom: "pdfAction" });
  triggerPDFAction(action);
};

const handleResetForm = (stores, action, extraParams) => {
  const overlayApis = getPageOverlayApis(stores, ACROFORM_CLIENT_NAME);
  if (overlayApis) {
    overlayApis.resetAcroform(action);
  }
  triggerPDFAction(action);
};

const handleHideAction = (stores, action) => {
  const { fieldNames = [], fieldIds = [], hide } = action.actionData;
  const overlayApis = getPageOverlayApis(stores, "acroform");
  if (!overlayApis) {
    return;
  }

  fieldNames.forEach(fieldName => {
    overlayApis.toggleAcroFormFieldDisplay({ fieldName, hide });
  });

  fieldIds.forEach(fieldId => {
    overlayApis.toggleAcroFormFieldDisplay({ fieldId, hide });
  });
};

/* This function should never be made async. It is crucial to maintain the synchronous nature of JS Actions */
const performAction = (stores, actions, extraParams, response = {}) => {
  if (isUndefined(actions)) {
    response.success = false;
    return;
  }

  for (let i = 0; i < actions.length; i++) {
    const action = actions[i];
    switch (action.command) {
      case "GoToLocation":
        handleGoToAction(stores, action, extraParams);
        break;

      case "GoToURL":
        handleURIAction(stores, action, extraParams);
        break;

      case "JavaScript":
        handleJavaScriptAction(stores, action, extraParams);
        break;

      case "ChangeZoomLevel":
        handleChangeZoomLevel(stores, action, extraParams);
        break;

      case "ChangePageViewModifier":
        handleChangePageViewModifier(stores, action, extraParams);
        break;

      case "GoToPage":
        handleGoToPage(stores, action, extraParams);
        break;

      case "UpdateSideBarPlugin":
        handleUpdateSideBarPlugin(stores, action, extraParams);
        break;

      case "SavePDF":
        handleSavePDF(stores, action, extraParams);
        break;

      case "ZoomIn":
        handleZoomIn(stores, action, extraParams);
        break;

      case "ZoomOut":
        handleZoomOut(stores, action, extraParams);
        break;

      case "Print":
        handlePrint(stores, action, extraParams);
        break;

      case "SubmitForm":
        handleSubmitForm(stores, action, extraParams);
        break;

      case "ResetForm":
        handleResetForm(stores, action, extraParams);
        break;

      case "Hide":
        handleHideAction(stores, action, extraParams);
        break;

      default:
        console.log("Found an unknown command!", action);
        break;
    }
  }
};

export async function initJSEngine(stores) {
  const jsOverridesWaitStart = performance.now();
  const supportPDFJavaScript = await getSupportPDFJavaScriptPromise(stores);
  const jsOverridesWaitEnd = performance.now();
  let jsEngine = null;
  if (supportPDFJavaScript) {
    jsEngine = await stores.appStore.instantiateJSEngine();
  }
  if (jsEngine) {
    const initialisationStatus = await jsEngine.init(stores);
    if (initialisationStatus === false) {
      logJSActionsData({
        JSEngineInitializationFailed: true
      });
    }
  }
  const jsEngineInitializationDone = performance.now();
  recordPerformanceTimings({
    jsOverridesWaitTime: jsOverridesWaitEnd - jsOverridesWaitStart,
    jsEngineInitTime: jsEngineInitializationDone - jsOverridesWaitEnd
  });
  return jsEngine;
}
